import { Box, Button, Typography } from '@mui/material';
import logo from 'assets/logos/logo';
import { getFileName } from 'components/@home/drawers/HrBotDrawer/pdf-utils';
import { array, object, string } from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';

const BUTTON_COLOR = '#6269E5';
const BORDER = '#dde2eb';
const PANEL_BG = '#f8f9fd';
const MUTED = '#6e7087';
const PAGE_BG = '#fafafa';

/**
 * Visual preview aligned with production mail `signatureInvitation.hbs` (outer padding like legacy
 * transactional templates such as `signupRequest.hbs`). Metadata (To / Subject) shown above the frame.
 */
const SendMessageEmailPreview = ({
  messageSubject = '',
  message,
  files = [],
  userItems,
  format,
  emailRecipients = [],
}) => {
  const authEmployee = useSelector(state => state.auth?.employee);
  const requesterName = useMemo(() => {
    const n = [authEmployee?.firstName, authEmployee?.surName]
      .filter(v => v != null && String(v).trim())
      .join(' ')
      .trim();
    return n || '—';
  }, [authEmployee?.firstName, authEmployee?.surName]);

  const sendBy = format?.sendBy || 'code';
  const file = useMemo(() => {
    const fromEmployee = files?.find(f => !f.error && f.employee?.[sendBy]);
    if (fromEmployee) return fromEmployee;
    return files?.find(f => !f.error && f.file) || { file: {} };
  }, [files, sendBy]);
  const docName = getFileName({ ...file, ...userItems }, format);
  const { size } = file.file || {};

  const trimmedMessage = (message || '').trim();
  const trimmedSubject = (messageSubject || '').trim();
  const subjectLine = useMemo(() => {
    return trimmedSubject || format?.name || '—';
  }, [trimmedSubject, format?.name]);

  const toRecipients = useMemo(
    () => (emailRecipients || []).filter(r => String(r.email || '').trim()),
    [emailRecipients],
  );

  const signerPreviewName = useMemo(() => {
    const named = toRecipients.map(r => r.name && String(r.name).trim()).find(Boolean);
    return named || '';
  }, [toRecipients]);

  return (
    <Box sx={{ width: '100%', maxWidth: 600, pointerEvents: 'none' }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          mb: 1,
        }}
      >
        {I18n.t('HrBot.SendStep.emailPreviewCaption')}
      </Typography>

      <Box sx={{ py: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          {I18n.t('HrBot.SendStep.emailPreviewSubject')}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
          {subjectLine}
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: PAGE_BG,
          borderRadius: 1,
          py: 2,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Box
          sx={{
            maxWidth: 600,
            mx: 'auto',
            bgcolor: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 5 },
              py: 3,
              textAlign: 'center',
              borderBottom: `3px solid ${BUTTON_COLOR}`,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Ommnio"
              sx={{ height: 40, mb: 2, display: 'inline-block' }}
            />
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontSize: 24,
                fontWeight: 700,
                color: '#2c2c2c',
                fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
              }}
            >
              {I18n.t('HrBot.SendStep.emailInvitePreviewTitle')}
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 3, sm: 5 }, py: 4 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 500,
                mb: 2,
                color: '#2c2c2c',
                fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
              }}
            >
              {signerPreviewName
                ? I18n.t('HrBot.SendStep.emailInvitePreviewGreetingNamed', {
                    signerName: signerPreviewName,
                  })
                : I18n.t('HrBot.SendStep.emailInvitePreviewGreetingAnonymous')}
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                lineHeight: 1.6,
                mb: 2,
                color: MUTED,
                fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
              }}
            >
              {I18n.t('HrBot.SendStep.emailInvitePreviewBody', { requesterName })}
            </Typography>

            <Box
              sx={{
                backgroundColor: PANEL_BG,
                border: `1px solid ${BORDER}`,
                borderRadius: '8px',
                p: 2.5,
                my: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#2c2c2c',
                  mb: 0.5,
                  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                }}
              >
                📄 {docName}
              </Typography>
              <Typography sx={{ fontSize: 14, color: MUTED }}>
                {I18n.t('HrBot.SendStep.emailInvitePreviewRequestedBy', { requesterName })}
              </Typography>
              {size > 0 ? (
                <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5 }}>
                  {(size / 1024).toFixed(1)} KB
                </Typography>
              ) : null}
            </Box>

            {trimmedMessage ? (
              <Box
                sx={{
                  backgroundColor: PANEL_BG,
                  border: `1px solid ${BORDER}`,
                  borderLeft: `3px solid ${BUTTON_COLOR}`,
                  borderRadius: '8px',
                  p: 2,
                  my: 2,
                  fontSize: 15,
                  lineHeight: 1.55,
                  color: MUTED,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                }}
              >
                {trimmedMessage}
              </Box>
            ) : null}

            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                component="div"
                variant="contained"
                disableElevation
                sx={{
                  backgroundColor: BUTTON_COLOR,
                  color: '#fff',
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontSize: 16,
                  pointerEvents: 'none',
                  cursor: 'default',
                }}
              >
                {I18n.t('HrBot.SendStep.emailInvitePreviewButton')}
              </Button>
            </Box>

            <Typography
              sx={{
                mt: 3,
                fontSize: 14,
                color: MUTED,
                lineHeight: 1.5,
                fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
              }}
            >
              {I18n.t('HrBot.SendStep.emailInvitePreviewHelp', { requesterName })}
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: PANEL_BG,
              px: { xs: 3, sm: 5 },
              py: 2,
              textAlign: 'center',
              borderTop: `1px solid ${BORDER}`,
            }}
          >
            <Typography sx={{ fontSize: 13, color: MUTED, m: 0 }}>
              {I18n.t('HrBot.SendStep.emailInvitePreviewFooter')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: MUTED, mt: 1, m: 0 }}>
              © {new Date().getFullYear()} Ommnio.{' '}
              {I18n.t('HrBot.SendStep.emailInvitePreviewCopyright')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

SendMessageEmailPreview.propTypes = {
  messageSubject: string,
  message: string.isRequired,
  files: array,
  userItems: object.isRequired,
  format: object.isRequired,
  emailRecipients: array,
};

export default SendMessageEmailPreview;
